import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  "en": {
    "seoTitle": "Okta Alternative: Monofor | Deployment, AI agents & pricing",
    "seoDescription": "Compare Monofor and Okta for SSO, MFA, governance, PAM and AI agent security. Review deployment options, licensing scope and source-linked feature details.",
    "heroEyebrow": "Okta alternative",
    "heroTitle": [
      "Identity on your terms.",
      "Compare the details."
    ],
    "heroBody": "Monosign brings SSO, MFA and governance to your own infrastructure or the cloud. Compare deployment, product coverage and AI agent controls, then validate the fit with your applications. Monosign starts at $4 per user per month; Monopam is licensed separately by concurrent session.",
    "reasons": [
      {
        "title": "Choose where identity runs",
        "body": "Monosign runs self-hosted or in the cloud. If identity data must stay on infrastructure you control, deployment is an early selection criterion."
      },
      {
        "title": "Understand what you are buying",
        "body": "Compare IAM, governance and PAM by product and license. Monofor connects Monosign, Monopam and Monosync in one platform; confirm each product’s scope in your quote."
      },
      {
        "title": "Test real access scenarios",
        "body": "Use your SaaS apps, legacy systems and AI tools in the evaluation. Test authentication, provisioning and policy enforcement as separate requirements."
      }
    ],
    "tableTitle": [
      "Monofor vs Okta,",
      "feature by feature."
    ],
    "tableBody": "Compare capabilities alongside their product, plan and deployment conditions. The AI rows distinguish agent identity management from runtime control of MCP tool calls.",
    "rows": [
      {
        "id": "deployment",
        "feature": "Self-hosted deployment",
        "detail": "Where the identity provider runs; evaluated separately from local connectors.",
        "monofor": "Self-hosted or cloud",
        "competitor": "Cloud identity platform",
        "sources": [
          {
            "label": "Monosign",
            "href": "/sign"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "sso",
        "feature": "SSO app catalog",
        "detail": "Check SSO and provisioning coverage for each target application.",
        "monofor": "7,000+ apps",
        "competitor": "Okta Integration Network",
        "sources": []
      },
      {
        "id": "mfa",
        "feature": "MFA with passkeys / FIDO2",
        "detail": "Authentication methods and adaptive access policies.",
        "monofor": true,
        "competitor": true,
        "sources": []
      },
      {
        "id": "governance",
        "feature": "Identity governance",
        "detail": "Access reviews, certification and separation of duties.",
        "monofor": "Monosign IGA",
        "competitor": "Essentials and above",
        "sources": [
          {
            "label": "Monofor",
            "href": "/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "pam",
        "feature": "Privileged access management",
        "detail": "Evaluate identity licensing and PAM licensing separately.",
        "monofor": "Monopam; concurrent-session license",
        "competitor": "Essentials: 2 PAM admins",
        "sources": [
          {
            "label": "Monofor",
            "href": "/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "legacy",
        "feature": "Legacy application access",
        "detail": "Protocol support, connector deployment and packaging vary by application.",
        "monofor": "RADIUS, LDAP Gateway, Access Gateway",
        "competitor": "RADIUS, LDAP Interface, Access Gateway",
        "sources": [
          {
            "label": "Okta LDAP",
            "href": "https://help.okta.com/en-us/content/topics/directory/ldap-interface-main.htm"
          },
          {
            "label": "Okta RADIUS",
            "href": "https://help.okta.com/oie/en-us/content/topics/integrations/okta_radius_app-gen-add-app.htm"
          },
          {
            "label": "Okta Access Gateway",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "directory",
        "feature": "Directory and source synchronization",
        "detail": "Check connector coverage for AD, LDAP, HR and custom data sources.",
        "monofor": "Monosync; AD, LDAP, HR, SQL",
        "competitor": "Universal Directory; AD, LDAP, HR",
        "sources": [
          {
            "label": "Monosync",
            "href": "/sync"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "pricing",
        "feature": "Entry price",
        "detail": "The Monosign entry price does not cover every Monofor product.",
        "monofor": "Monosign: from $4 / user / month",
        "competitor": "Starter: $6 / user / month; billed annually",
        "sources": [
          {
            "label": "Monofor",
            "href": "/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "itdr",
        "feature": "Identity threat detection and response",
        "detail": "Evaluate session risk, signal sharing and response coverage.",
        "monofor": "Monosign ITDR; SSF/CAEP",
        "competitor": "ITP: Professional and above; varies below",
        "sources": [
          {
            "label": "Monofor",
            "href": "/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "ai-agent-identity",
        "feature": "AI agent identity and lifecycle",
        "detail": "Which agent acts for whom? Identity, ownership and access lifecycle.",
        "monofor": "Monosign AI Agents: agent identity, ownership, time-limited access",
        "competitor": "Okta for AI Agents: discovery, registry and lifecycle",
        "sources": [
          {
            "label": "Monosign AI Agents",
            "href": "/sign/ai-agents"
          },
          {
            "label": "Okta for AI Agents",
            "href": "https://www.okta.com/products/govern-ai-agent-identity/"
          }
        ]
      },
      {
        "id": "mcp-gateway",
        "feature": "Controlled access to MCP tools",
        "detail": "Identity and policy checks during agent tool calls; release status is stated separately.",
        "monofor": "Monosign MCP Gateway: tool scopes, policy checks, audit trail",
        "competitor": "Agent Gateway: research release; MCP Bridge via Professional Services",
        "sources": [
          {
            "label": "Monosign MCP Gateway",
            "href": "/sign/mcp-gateway"
          },
          {
            "label": "Okta Agent Gateway",
            "href": "https://www.okta.com/blog/product-innovation/agent-gateway-runtime-governance/"
          }
        ]
      },
      {
        "id": "identity-intelligence",
        "feature": "Identity visibility and risk analysis",
        "detail": "Verify effective access, permission relationships and risky access-path coverage.",
        "monofor": "Identity Intelligence: effective access and blast radius",
        "competitor": "ISPM: identity and permission risk analysis",
        "sources": [
          {
            "label": "Monofor Identity Intelligence",
            "href": "/sign/identity-intelligence"
          },
          {
            "label": "Okta ISPM",
            "href": "https://learning.okta.com/set-up-identity-security-posture-management"
          }
        ]
      },
      {
        "id": "support",
        "feature": "Customer support",
        "detail": "Confirm support hours and service levels in your quote.",
        "monofor": "Included",
        "competitor": "24×5 online; Premier extra",
        "sources": [
          {
            "label": "Monofor",
            "href": "/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "trial",
        "feature": "Free trial",
        "detail": "Test application and policy compatibility before purchasing.",
        "monofor": true,
        "competitor": true,
        "sources": []
      }
    ],
    "faqs": [
      {
        "q": "When is Monofor an alternative to Okta?",
        "a": "Consider Monofor when self-hosted identity, legacy application access or a shared IAM, IGA and PAM platform matters to your organization. Validate each required integration and control in a trial; catalog size alone does not establish feature parity."
      },
      {
        "q": "Does the $4 Monosign price include Monopam?",
        "a": "No. Monosign starts at $4 per user per month. Monopam uses separate concurrent-session licensing, and Monosync is quoted for your environment. Check the pricing page and request a quote for your product mix."
      },
      {
        "q": "Does Okta support AI agents and MCP?",
        "a": "Yes. Okta for AI Agents covers agent discovery, registration and lifecycle controls. Its Agent Gateway announcement describes a research release for runtime MCP controls. Compare release status, supported tools and policy behavior separately from agent identity management."
      },
      {
        "q": "How should we test a migration from Okta?",
        "a": "Start with a pilot application and an upstream identity-provider connection. Validate SAML or OIDC configuration, provisioning, MFA policies and directory synchronization before migrating additional applications."
      },
      {
        "q": "How was this comparison prepared?",
        "a": "Monofor publishes this comparison. The linked Okta pricing, AI agent, gateway and integration documentation supports the corresponding rows. Product coverage is not a guarantee that a capability is included in an entry plan; confirm licensing and availability with each vendor."
      }
    ]
  },
  "tr": {
    "seoTitle": "Okta Alternatifi: Monofor | Kurulum, AI ajanları ve fiyatlandırma",
    "seoDescription": "Monofor ve Okta’yı SSO, MFA, yönetişim, PAM ve AI ajan güvenliği açısından karşılaştırın. Kurulum seçenekleri, lisans kapsamı ve kaynaklı özellik açıklamaları.",
    "heroEyebrow": "Okta alternatifi",
    "heroTitle": [
      "Kimlik altyapınız, sizin kararınız.",
      "Farkı detaylarda görün."
    ],
    "heroBody": "Monosign; SSO, MFA ve yönetişimi kendi altyapınızda veya bulutta sunar. Kurulum seçeneklerini, ürün kapsamını ve AI ajan kontrollerini karşılaştırın; uyumu kendi uygulamalarınızla doğrulayın. Monosign kullanıcı başına aylık 4 $’dan başlar; Monopam eşzamanlı oturum üzerinden ayrıca lisanslanır.",
    "reasons": [
      {
        "title": "Kimliğin nerede çalışacağını seçin",
        "body": "Monosign kendi altyapınızda veya bulutta çalışır. Kimlik verisinin kontrol ettiğiniz altyapıda kalması gerekiyorsa kurulum modeli ilk değerlendirme ölçütlerinden biridir."
      },
      {
        "title": "Neyi satın aldığınızı netleştirin",
        "body": "IAM, yönetişim ve PAM’i ürün ve lisans bazında değerlendirin. Monofor; Monosign, Monopam ve Monosync’i aynı platformda bir araya getirir. Her ürünün kapsamını teklifinizde doğrulayın."
      },
      {
        "title": "Gerçek erişim senaryolarını deneyin",
        "body": "Değerlendirmeye kendi SaaS uygulamalarınızı, eski sistemlerinizi ve AI araçlarınızı dahil edin. Kimlik doğrulama, kullanıcı sağlama ve politika uygulamasını ayrı gereksinimler olarak test edin."
      }
    ],
    "tableTitle": [
      "Monofor ve Okta,",
      "özellik özellik."
    ],
    "tableBody": "Her özelliği ürün, paket ve kurulum koşullarıyla birlikte okuyun. AI satırlarında ajan kimliğinin yönetimi ile MCP araç çağrılarının çalışma anında kontrolü ayrı ayrı açıklanır.",
    "rows": [
      {
        "id": "deployment",
        "feature": "Kendi altyapınızda kurulum",
        "detail": "Kimlik sağlayıcısının nerede çalıştığı; yerel bağlayıcılardan ayrı değerlendirilir.",
        "monofor": "Self-hosted veya bulut",
        "competitor": "Bulut kimlik platformu",
        "sources": [
          {
            "label": "Monosign",
            "href": "/tr/sign"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "sso",
        "feature": "SSO uygulama kataloğu",
        "detail": "Hedef uygulamanın SSO ve kullanıcı sağlama kapsamını ayrı ayrı doğrulayın.",
        "monofor": "7.000+ uygulama",
        "competitor": "Okta Integration Network",
        "sources": []
      },
      {
        "id": "mfa",
        "feature": "Passkey / FIDO2 ile MFA",
        "detail": "Kimlik doğrulama yöntemleri ve uyarlanabilir erişim politikaları.",
        "monofor": true,
        "competitor": true,
        "sources": []
      },
      {
        "id": "governance",
        "feature": "Kimlik yönetişimi",
        "detail": "Erişim incelemeleri, sertifikasyon ve görev ayrılığı.",
        "monofor": "Monosign IGA",
        "competitor": "Essentials ve üstü",
        "sources": [
          {
            "label": "Monofor",
            "href": "/tr/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "pam",
        "feature": "Ayrıcalıklı erişim yönetimi",
        "detail": "Kimlik lisansıyla PAM lisansının kapsamını ayrı değerlendirin.",
        "monofor": "Monopam; eşzamanlı oturum lisansı",
        "competitor": "Essentials: 2 PAM yöneticisi",
        "sources": [
          {
            "label": "Monofor",
            "href": "/tr/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "legacy",
        "feature": "Eski uygulamalara erişim",
        "detail": "Protokol desteği, bağlayıcı kurulumu ve paket koşulları uygulamaya göre değişir.",
        "monofor": "RADIUS, LDAP Gateway, Access Gateway",
        "competitor": "RADIUS, LDAP Interface, Access Gateway",
        "sources": [
          {
            "label": "Okta LDAP",
            "href": "https://help.okta.com/en-us/content/topics/directory/ldap-interface-main.htm"
          },
          {
            "label": "Okta RADIUS",
            "href": "https://help.okta.com/oie/en-us/content/topics/integrations/okta_radius_app-gen-add-app.htm"
          },
          {
            "label": "Okta Access Gateway",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "directory",
        "feature": "Dizin ve kaynak eşitleme",
        "detail": "AD, LDAP, İK ve özel veri kaynakları için bağlayıcı kapsamını kontrol edin.",
        "monofor": "Monosync; AD, LDAP, İK, SQL",
        "competitor": "Universal Directory; AD, LDAP, HR",
        "sources": [
          {
            "label": "Monosync",
            "href": "/tr/sync"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "pricing",
        "feature": "Başlangıç fiyatı",
        "detail": "Monosign başlangıç fiyatı tüm Monofor ürünlerini kapsamaz.",
        "monofor": "Monosign: 4 $ / kullanıcı / ay’dan",
        "competitor": "Starter: 6 $ / kullanıcı / ay; yıllık fatura",
        "sources": [
          {
            "label": "Monofor",
            "href": "/tr/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "itdr",
        "feature": "Kimlik tehditlerini tespit ve müdahale",
        "detail": "Oturum riski, sinyal paylaşımı ve müdahale kapsamını değerlendirin.",
        "monofor": "Monosign ITDR; SSF/CAEP",
        "competitor": "ITP: Professional ve üstü; alt paketlerde değişir",
        "sources": [
          {
            "label": "Monofor",
            "href": "/tr/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "ai-agent-identity",
        "feature": "AI ajan kimliği ve yaşam döngüsü",
        "detail": "Hangi ajan, kimin adına çalışıyor? Kimlik, sahiplik ve erişim yaşam döngüsü.",
        "monofor": "Monosign AI Agents: ajan kimliği, sahiplik, süreli yetki",
        "competitor": "Okta for AI Agents: keşif, kayıt ve yaşam döngüsü",
        "sources": [
          {
            "label": "Monosign AI Agents",
            "href": "/tr/sign/ai-agents"
          },
          {
            "label": "Okta for AI Agents",
            "href": "https://www.okta.com/products/govern-ai-agent-identity/"
          }
        ]
      },
      {
        "id": "mcp-gateway",
        "feature": "MCP araçlarına kontrollü erişim",
        "detail": "Ajanın araç çağrısı sırasında kimlik ve politika kontrolü; yayın durumu ayrıca belirtilir.",
        "monofor": "Monosign MCP Gateway: araç kapsamı, politika kontrolü, denetim kaydı",
        "competitor": "Agent Gateway: araştırma sürümü; MCP Bridge: Professional Services üzerinden",
        "sources": [
          {
            "label": "Monosign MCP Gateway",
            "href": "/tr/sign/mcp-gateway"
          },
          {
            "label": "Okta Agent Gateway",
            "href": "https://www.okta.com/blog/product-innovation/agent-gateway-runtime-governance/"
          }
        ]
      },
      {
        "id": "identity-intelligence",
        "feature": "Kimlik görünürlüğü ve risk analizi",
        "detail": "Etkin erişim, yetki ilişkileri ve riskli erişim yollarının kapsamını doğrulayın.",
        "monofor": "Identity Intelligence: etkin erişim ve blast radius",
        "competitor": "ISPM: kimlik ve yetki risk analizi",
        "sources": [
          {
            "label": "Monofor Identity Intelligence",
            "href": "/sign/identity-intelligence"
          },
          {
            "label": "Okta ISPM",
            "href": "https://learning.okta.com/set-up-identity-security-posture-management"
          }
        ]
      },
      {
        "id": "support",
        "feature": "Müşteri desteği",
        "detail": "Destek saatleri ve hizmet seviyesini teklifinizde doğrulayın.",
        "monofor": "Dahil",
        "competitor": "24×5 çevrimiçi; Premier ek ücretli",
        "sources": [
          {
            "label": "Monofor",
            "href": "/tr/pricing"
          },
          {
            "label": "Okta",
            "href": "https://www.okta.com/pricing/"
          }
        ]
      },
      {
        "id": "trial",
        "feature": "Ücretsiz deneme",
        "detail": "Satın almadan önce uygulama ve politika uyumluluğunu test edin.",
        "monofor": true,
        "competitor": true,
        "sources": []
      }
    ],
    "faqs": [
      {
        "q": "Monofor hangi durumlarda Okta’ya alternatiftir?",
        "a": "Kendi altyapınızda kimlik yönetimi, eski uygulamalara erişim veya ortak IAM, IGA ve PAM platformu öncelikliyse Monofor’u değerlendirin. İhtiyacınız olan her entegrasyonu ve kontrolü deneme ortamında doğrulayın; katalog büyüklüğü tek başına özellik eşitliği anlamına gelmez."
      },
      {
        "q": "4 $’lık Monosign fiyatına Monopam dahil mi?",
        "a": "Hayır. Monosign kullanıcı başına aylık 4 $’dan başlar. Monopam eşzamanlı oturum üzerinden ayrıca lisanslanır; Monosync için ortamınıza göre teklif hazırlanır. Ürün bileşiminiz için fiyatlandırma sayfasını inceleyip teklif alın."
      },
      {
        "q": "Okta AI ajanlarını ve MCP’yi destekliyor mu?",
        "a": "Evet. Okta for AI Agents; ajan keşfi, kaydı ve yaşam döngüsü kontrollerini kapsar. Agent Gateway duyurusu, çalışma anındaki MCP kontrolleri için bir araştırma sürümünü tanımlar. Yayın durumunu, desteklenen araçları ve politika davranışını ajan kimliği yönetiminden ayrı değerlendirin."
      },
      {
        "q": "Okta’dan geçişi nasıl test etmeliyiz?",
        "a": "Bir pilot uygulama ve üst kimlik sağlayıcısı bağlantısıyla başlayın. Diğer uygulamaları taşımadan önce SAML veya OIDC yapılandırmasını, kullanıcı sağlamayı, MFA politikalarını ve dizin eşitlemeyi doğrulayın."
      },
      {
        "q": "Bu karşılaştırma nasıl hazırlandı?",
        "a": "Bu karşılaştırmayı Monofor yayımlar. İlgili satırlardaki Okta fiyatlandırma, AI ajanı, geçit ve entegrasyon belgeleri açıklamaların kaynaklarıdır. Ürün desteği, özelliğin başlangıç paketine dahil olduğunu garanti etmez; lisans ve kullanılabilirlik koşullarını her tedarikçiyle doğrulayın."
      }
    ]
  }
}

type Props = { locale: string }

export default function OktaAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      competitor="Okta"
      locale={locale}
      bilingual
      path="/compare/okta-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      comparisonNote={locale === 'tr'
        ? 'Okta fiyatlandırma, AI ajanı, geçit ve entegrasyon kaynakları 22 Eylül 2026 tarihinde gözden geçirildi. Diğer yetenekleri deneme ortamında doğrulayın.'
        : 'Okta pricing, AI agent, gateway and integration sources reviewed on September 22, 2026. Validate other capabilities in your trial.'}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
